import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, LogOut, Search, Star, MessageSquare, CreditCard, CheckCircle, XCircle, Heart, Filter, BellRing, Trash2, Briefcase, Settings, AlertCircle, Users, Bookmark, ToggleLeft, ToggleRight, Zap, ChevronRight, Coins, ChevronDown, ChevronUp, Loader2, MapPin, Calendar, Phone, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import EmployerAnalytics from '../components/EmployerAnalytics';
import HireModal from '../components/HireModal';
import EmploymentHistory from '../components/EmploymentHistory';
import ConfirmedJobsSection from '../components/ConfirmedJobsSection';
import NotificationSettings from '../components/NotificationSettings';
import CompanyManagement from '../components/CompanyManagement';
import PendingReviews from '../components/PendingReviews';
import ReferralCard from '../components/ReferralCard';
import { UrgentRequestForm } from '../components/UrgentRequests';
import { CreditBalance, FreePreviewBanner } from '../components/CreditSystem';
import PartnerPlacement from '../components/PartnerPlacement';
import BillingManagement, { CreditPurchaseModal, CreditPurchaseSuccess } from '../components/BillingManagement';
import { QuickResponderBadge, ResponseTimeLeaderboard } from '../components/ResponseLeaderboard';
import { useSiteContent } from '../hooks/useSiteContent';
import HiringCountdownBanner, { useHiringStatus } from '../components/HiringCountdownBanner';

import { BACKEND_URL } from '../config/api';
import { toLocalYMD, todayYMD } from '../utils/dateHelpers';
import DashboardBanner from '../components/DashboardBanner';
import RecentQuoteRequestsPanel from '../components/RecentQuoteRequestsPanel';
import { shouldAutoStartTour, startTour } from '../components/DemoTourOverlay';
import { useOnboardingGate } from '../hooks/useOnboardingGate';

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  useOnboardingGate();
  const { get } = useSiteContent();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [tradespeople, setTradespeople] = useState([]);
  const [supplyDemand, setSupplyDemand] = useState(null);
  const [trades, setTrades] = useState([]);
  const [plans, setPlans] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('search');
  const [saveAlert, setSaveAlert] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [searchAlerts, setSearchAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showSubscriptionModal] = useState(false);
  const [showNoChatModal, setShowNoChatModal] = useState(false);
  const [pendingContact, setPendingContact] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [credentialTypes, setCredentialTypes] = useState([]);
  
  // Credit Purchase State
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [creditSettings, setCreditSettings] = useState(null);
  const [purchaseSessionId, setPurchaseSessionId] = useState(null);
  
  const [searchParams, setSearchParams] = useState({
    trade: '',
    location: '',
    start_date: '',
    end_date: '',
    min_rating: '',
    min_rate: '',
    max_rate: '',
    transport: '',
    has_credentials: false,
    credential_type: '',
    include_limited: false,
    available_now: false,
    verified_only: false,
    worker_type: '',
    // Multi-select engagement filters — checkboxes.
    // Each is a boolean; selected items are combined into the backend query.
    engagement_day_rate: false,
    engagement_price_work: false,
    engagement_labour_only: false,
    engagement_supply_and_fit: false,
    engagement_subcontractor: false,
    engagement_team: false,
    engagement_ltd: false,
  });
  const [availabilityFilters, setAvailabilityFilters] = useState({
    availableTomorrow: false,
    availableThisWeek: false
  });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedTradesperson, setSelectedTradesperson] = useState(null);
  const [showHireModal, setShowHireModal] = useState(false);
  const [hireTarget, setHireTarget] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [reviewData, setReviewData] = useState({
    punctuality: 5,
    professionalism: 5,
    quality: 5,
    comment: ''
  });
  
  // Urgent Request Modal State
  const [showUrgentRequestModal, setShowUrgentRequestModal] = useState(false);
  
  // Search states
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [useDateRange, setUseDateRange] = useState(false);

  // Helper: Check if a person is available on a specific date
  const isAvailableOnDate = (person, targetDate) => {
    const targetStr = toLocalYMD(targetDate);
    
    if (person.availability_mode === 'available_now') {
      return true;
    }
    if (person.availability_mode === 'available_from' && person.available_from_date) {
      return person.available_from_date <= targetStr;
    }
    if (person.availability_mode === 'custom_dates' && person.available_dates?.length > 0) {
      return person.available_dates.includes(targetStr);
    }
    return false;
  };

  // Helper: Check if a person is available within a date range (any date)
  const isAvailableInRange = (person, startDate, endDate) => {
    if (person.availability_mode === 'available_now') {
      return true;
    }
    if (person.availability_mode === 'available_from' && person.available_from_date) {
      return person.available_from_date <= toLocalYMD(endDate);
    }
    if (person.availability_mode === 'custom_dates' && person.available_dates?.length > 0) {
      const start = toLocalYMD(startDate);
      const end = toLocalYMD(endDate);
      return person.available_dates.some(d => d >= start && d <= end);
    }
    return false;
  };

  // Filter and sort tradespeople based on availability filters
  const getFilteredAndSortedResults = (results) => {
    let filtered = [...results];
    
    // Apply availability filters (client-side)
    if (availabilityFilters.availableTomorrow || availabilityFilters.availableThisWeek) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      filtered = filtered.filter(person => {
        if (availabilityFilters.availableTomorrow && availabilityFilters.availableThisWeek) {
          // Both checked: must be available tomorrow (stricter)
          return isAvailableOnDate(person, tomorrow);
        }
        if (availabilityFilters.availableTomorrow) {
          return isAvailableOnDate(person, tomorrow);
        }
        if (availabilityFilters.availableThisWeek) {
          return isAvailableInRange(person, tomorrow, weekEnd);
        }
        return true;
      });
    }
    
    // Sort: available_now first, then nearest available_from_date, then rating
    filtered.sort((a, b) => {
      // Priority 1: available_now comes first
      const aIsNow = a.availability_mode === 'available_now';
      const bIsNow = b.availability_mode === 'available_now';
      if (aIsNow && !bIsNow) return -1;
      if (!aIsNow && bIsNow) return 1;
      
      // Priority 2: nearest available_from_date
      if (a.availability_mode === 'available_from' && b.availability_mode === 'available_from') {
        const aDate = a.available_from_date || '9999-99-99';
        const bDate = b.available_from_date || '9999-99-99';
        if (aDate !== bDate) return aDate.localeCompare(bDate);
      }
      // available_from before custom_dates
      if (a.availability_mode === 'available_from' && b.availability_mode === 'custom_dates') return -1;
      if (a.availability_mode === 'custom_dates' && b.availability_mode === 'available_from') return 1;
      
      // Priority 3: rating (descending)
      const aRating = a.average_rating || 0;
      const bRating = b.average_rating || 0;
      return bRating - aRating;
    });
    
    return filtered;
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messages/unread-count`, {
        withCredentials: true
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.warn('EmployerDashboard: failed to fetch unread count', error.message);
    }
  }, []);

  const fetchTrades = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trades`);
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades');
    }
  }, []);

  const fetchSavedSearches = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/saved-searches`, {
        withCredentials: true
      });
      setSavedSearches(response.data);
    } catch (error) {
      console.error('Failed to fetch saved searches');
    }
  }, []);

  useEffect(() => {
    // Initial auth check - runs once on mount
    const initAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        
        if (response.data.user_type !== 'employer') {
          navigate('/login');
          return;
        }
        
        setUser(response.data);
        // Auto-start demo tour for demo employers on first login
        if (shouldAutoStartTour(response.data)) {
          setTimeout(() => startTour(), 1500);
        }
        fetchTrades();
        fetchSavedSearches();
        fetchUnreadCount();
        fetchSubscription();
        fetchSubscriptionStatus();
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
    
    // Poll for unread messages every 30 seconds
    const pollInterval = setInterval(fetchUnreadCount, 30000);
    
    // Listen for subscription modal trigger from child components
    const handleOpenSubscriptionModal = () => navigate('/plans');
    window.addEventListener('openSubscriptionModal', handleOpenSubscriptionModal);
    
    // Handle credit purchase return (supports both old and new URL params)
    const paymentStatus = urlSearchParams.get('payment');
    const creditPurchase = urlSearchParams.get('credit_purchase');
    const sessionId = urlSearchParams.get('session_id');
    const openCredits = urlSearchParams.get('openCredits');
    
    // Auto-open credit purchase modal if redirected from profile
    if (openCredits === 'true') {
      setShowCreditPurchase(true);
      // Clean up the URL param
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    if ((paymentStatus === 'success' || creditPurchase === 'success') && sessionId) {
      setPurchaseSessionId(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('openSubscriptionModal', handleOpenSubscriptionModal);
    };
  }, [navigate, urlSearchParams, fetchTrades, fetchSavedSearches, fetchUnreadCount]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'employer') {
        navigate('/login');
        return;
      }
      
      // Check if user is waitlisted
      if (response.data.region_status === 'waitlisted') {
        navigate('/waitlist-status');
        return;
      }
      
      setUser(response.data);
      // Fire all fetches in parallel, then mark loading done
      await Promise.allSettled([
        fetchSubscription(),
        fetchSubscriptionStatus(),
        fetchTrades(),
        fetchNotifications(),
        fetchSearchAlerts(),
        fetchPlans(),
        fetchUnreadCount(),
        fetchActivityFeed(),
        fetchSavedSearches(),
      ]);
    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employer/subscription`, {
        withCredentials: true
      });
      setSubscription(response.data);
    } catch (error) {
      console.warn('EmployerDashboard: failed to fetch subscription', error.message);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employer/subscription-status`, {
        withCredentials: true
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status');
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription-plans`);
      // Filter to employer plans only
      const employerPlans = response.data.filter(p => p.plan_type === 'employer');
      setPlans(employerPlans);
    } catch (error) {
      console.error('Failed to fetch plans');
    }
  };

  const fetchCreditSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/credits/settings`);
      setCreditSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch credit settings');
    }
  };

  // Fetch credit settings when opening purchase modal
  const handleOpenCreditPurchase = async () => {
    await fetchCreditSettings();
    setShowCreditPurchase(true);
  };

  const handlePurchaseComplete = () => {
    setPurchaseSessionId(null);
    // Clear the URL params
    navigate('/employer/dashboard', { replace: true });
    // Refresh user data to get updated credit balance
    checkAuth();
    toast.success('Credits added to your account!');
  };

  const checkChatAllowance = async (tradespersonId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/employer/check-chat-allowance?tradesperson_id=${tradespersonId}`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      return { can_chat: false, reason: 'error' };
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/favorites`, {
        withCredentials: true
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites');
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/notifications/unread-count`, { withCredentials: true })
      ]);
      setNotifications(notifResponse.data);
      setNotificationCount(countResponse.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchSearchAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/search-alerts`, {
        withCredentials: true
      });
      setSearchAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch search alerts');
    }
  };

  const fetchActivityFeed = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/activity/recent`, {
        withCredentials: true
      });
      setActivityFeed(response.data);
    } catch (error) {
      console.error('Failed to fetch activity feed');
    }
  };

  // Helper: Format relative time for activity feed
  const formatRelativeTime = (isoDate) => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/search-alerts/${alertId}`, {
        withCredentials: true
      });
      setSearchAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleSaveSearch = async () => {
    if (!searchParams.trade || !searchParams.location) {
      toast.error('Enter both a trade and location to save this search');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/saved-searches`, {
        trade_type: searchParams.trade,
        area_key: searchParams.location,
        start_date: searchParams.start_date || null,
        end_date: searchParams.end_date || null,
        filters_json: {
          availableTomorrow: availabilityFilters.availableTomorrow,
          availableThisWeek: availabilityFilters.availableThisWeek
        }
      }, { withCredentials: true });
      
      toast.success('Search saved! You\'ll be notified when matching tradespeople become available.');
      fetchSavedSearches();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('This search is already saved');
      } else {
        toast.error('Failed to save search');
      }
    }
  };

  const handleToggleSavedSearch = async (searchId, currentEnabled) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/saved-searches/${searchId}`, {
        is_enabled: !currentEnabled
      }, { withCredentials: true });
      
      setSavedSearches(prev => prev.map(s => 
        s.search_id === searchId ? { ...s, is_enabled: !currentEnabled } : s
      ));
      toast.success(currentEnabled ? 'Notifications paused' : 'Notifications enabled');
    } catch (error) {
      toast.error('Failed to update saved search');
    }
  };

  const handleDeleteSavedSearch = async (searchId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/saved-searches/${searchId}`, {
        withCredentials: true
      });
      setSavedSearches(prev => prev.filter(s => s.search_id !== searchId));
      toast.success('Saved search removed');
    } catch (error) {
      toast.error('Failed to delete saved search');
    }
  };

  const formatSavedSearchLabel = (search) => {
    const parts = [];
    if (search.trade_type) parts.push(search.trade_type);
    if (search.area_key) parts.push(search.area_key);
    if (search.start_date && search.end_date) {
      const start = new Date(search.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      const end = new Date(search.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      parts.push(`${start} - ${end}`);
    } else if (search.filters_json?.availableTomorrow) {
      parts.push('Tomorrow');
    } else if (search.filters_json?.availableThisWeek) {
      parts.push('This week');
    }
    return parts.join(' • ') || 'All tradespeople';
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {}, {
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const handleSearch = async () => {
    // Prevent duplicate submissions
    if (isSearching) return;
    
    // Clear previous error
    setSearchError('');
    
    // Validation - trade is required
    if (!searchParams.trade) {
      setSearchError('Please select a trade type');
      toast.error('Please select a trade type');
      return;
    }
    
    setIsSearching(true);
    
    try {
      const params = new URLSearchParams();
      if (searchParams.trade) params.append('trade', searchParams.trade);
      if (searchParams.location) params.append('location', searchParams.location);
      if (searchParams.start_date) params.append('start_date', searchParams.start_date);
      if (searchParams.end_date && useDateRange) params.append('end_date', searchParams.end_date);
      if (searchParams.min_rating) params.append('min_rating', searchParams.min_rating);
      if (searchParams.min_rate) params.append('min_rate', searchParams.min_rate);
      if (searchParams.max_rate) params.append('max_rate', searchParams.max_rate);
      if (searchParams.transport) params.append('transport', searchParams.transport);
      if (searchParams.has_credentials) params.append('has_credentials', 'true');
      if (searchParams.credential_type) params.append('credential_type', searchParams.credential_type);
      if (searchParams.include_limited) params.append('include_limited', 'true');
      if (searchParams.available_now) params.append('on_call_only', 'true');
      if (searchParams.worker_type) params.append('worker_type', searchParams.worker_type);

      // Engagement-type multi-select — compose worker_type (CSV) and
      // preferred_work_type (CSV) from the selected checkboxes.
      const selectedWorkerTypes = [];
      if (searchParams.engagement_subcontractor) selectedWorkerTypes.push('self_employed_subcontractor');
      if (searchParams.engagement_team) selectedWorkerTypes.push('small_trade_team_gang');
      if (searchParams.engagement_ltd) selectedWorkerTypes.push('limited_company_trade_contractor');
      if (selectedWorkerTypes.length > 0 && !searchParams.worker_type) {
        params.set('worker_type', selectedWorkerTypes.join(','));
      }
      const selectedPreferred = [];
      if (searchParams.engagement_day_rate) selectedPreferred.push('day_rate');
      if (searchParams.engagement_price_work) selectedPreferred.push('price_work');
      if (searchParams.engagement_labour_only) selectedPreferred.push('labour_only');
      if (searchParams.engagement_supply_and_fit) selectedPreferred.push('supply_and_fit');
      if (selectedPreferred.length > 0) {
        params.append('preferred_work_type', selectedPreferred.join(','));
      }
      if (saveAlert && searchParams.start_date && searchParams.end_date) {
        params.append('save_alert', 'true');
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/tradespeople/search?${params}`, {
        withCredentials: true
      });
      
      // Handle new response structure with supply/demand data
      if (response.data.profiles) {
        setTradespeople(response.data.profiles);
        setSupplyDemand(response.data.supply_demand);
      } else {
        // Fallback for old response format
        setTradespeople(response.data);
        setSupplyDemand(null);
      }
      
      if (saveAlert && searchParams.start_date && searchParams.end_date) {
        toast.success('Search alert saved! You\'ll be notified when matching tradespeople become available.');
        setSaveAlert(false);
        fetchSearchAlerts();
      }
      
      const resultsCount = response.data.profiles ? response.data.profiles.length : response.data.length;
      if (resultsCount === 0) {
        toast.info('No tradespeople found matching your criteria. Try adjusting your filters.');
      }
    } catch (error) {
      toast.error('Search failed. Please try again.');
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };
  
  // Auto-format postcode to uppercase with proper spacing
  const formatPostcode = (value) => {
    // Remove all spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    // UK postcodes: outward code (2-4 chars) + inward code (3 chars)
    // Insert space before last 3 characters if length > 3
    if (cleaned.length > 3) {
      return cleaned.slice(0, -3) + ' ' + cleaned.slice(-3);
    }
    return cleaned;
  };
  
  const handleLocationChange = (e) => {
    const value = e.target.value;
    // Check if it looks like a postcode (starts with letter, contains numbers)
    const looksLikePostcode = /^[A-Za-z][A-Za-z0-9]*/.test(value.replace(/\s/g, ''));
    if (looksLikePostcode && value.length >= 2) {
      setSearchParams({ ...searchParams, location: formatPostcode(value) });
    } else {
      setSearchParams({ ...searchParams, location: value });
    }
  };

  const toggleFavorite = async (tradespersonId, currentState) => {
    try {
      if (currentState) {
        await axios.delete(`${BACKEND_URL}/api/favorites/${tradespersonId}`, {
          withCredentials: true
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${BACKEND_URL}/api/favorites/${tradespersonId}`, {}, {
          withCredentials: true
        });
        toast.success('Added to favorites');
      }
      
      // Update local state
      setTradespeople(prev => prev.map(tp => 
        tp.user_id === tradespersonId ? { ...tp, is_favorite: !currentState } : tp
      ));
      
      // Refresh favorites if on favorites tab
      if (activeTab === 'favorites') {
        fetchFavorites();
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout`,
        {
          plan_id: planId,
          host_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate checkout');
    }
  };

  const handleContactTradesperson = async (tradesperson) => {
    // Free marketplace: no subscription gate, no credit gate. Open the
    // conversation page with the full recipient object so the Messages
    // route can resolve the correct worker even if `user_info` is absent.
    if (!tradesperson?.user_id) {
      toast.error('Unable to open conversation — missing worker ID.');
      return;
    }
    const recipient = tradesperson.user_info || {
      user_id: tradesperson.user_id,
      name: tradesperson.name,
      trade: tradesperson.trade,
    };
    navigate('/messages', {
      state: {
        recipient,
        recipientId: tradesperson.user_id,
        autoOpen: true,
      },
    });
  };

  const handleLeaveReview = (tradesperson) => {
    setSelectedTradesperson(tradesperson);
    setShowReviewModal(true);
  };

  const submitReview = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/reviews`,
        {
          tradesperson_id: selectedTradesperson.user_id,
          employer_id: user.user_id,
          ...reviewData
        },
        { withCredentials: true }
      );
      
      toast.success('Review submitted!');
      setShowReviewModal(false);
      setReviewData({ punctuality: 5, professionalism: 5, quality: 5, comment: '' });
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hiring Countdown Banner - shown when hiring is disabled */}
      <HiringCountdownBanner />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-orange-500" data-testid="dashboard-logo" />
            <h1 className="text-xl font-bold text-slate-900">OnsiteHand</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600" data-testid="user-name">Hi, {user?.name}</span>
            
            {/* Credit Balance Display */}
            {/* FREE MARKETPLACE: compact credit balance hidden. */}
            {false && <CreditBalance compact={true} />}
            
            {/* Demo Tour Replay Button - only for demo users */}
            {user?.is_demo && (
              <button
                onClick={() => startTour()}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-violet-600 hover:to-purple-700 transition-all shadow-sm"
                data-testid="start-demo-tour-btn"
              >
                <Play className="h-3.5 w-3.5" />
                Demo Tour
              </button>
            )}

            <button
              onClick={() => navigate('/change-password')}
              className="btn-secondary text-sm"
              data-testid="change-password-btn"
            >
              Change Password
            </button>
            <button
              onClick={() => navigate('/messages')}
              className="btn-secondary text-sm flex items-center gap-2 relative"
              data-testid="messages-btn"
            >
              <MessageSquare className="h-4 w-4" />
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="unread-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {/* FREE MARKETPLACE: Buy Credits / Subscribe-first buttons removed. */}
            <button
              onClick={() => { setShowNotificationsModal(true); fetchNotifications(); }}
              className="btn-secondary text-sm flex items-center gap-2 relative"
              data-testid="notifications-btn"
            >
              <BellRing className="h-4 w-4" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="settings-btn"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DashboardBanner target="employer" />
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <img 
              src="/icons/onsitehand-icon.png" 
              alt="OnsiteHand" 
              className="h-16 w-16 rounded-xl shadow-md"
            />
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2 font-heading uppercase tracking-tight" data-testid="dashboard-title">Employer Dashboard</h2>
              <p className="text-slate-600">Find and hire skilled tradespeople</p>
            </div>
          </div>
        </div>

        {/* FREE MARKETPLACE: Subscription Status card removed. */}

        {/* Analytics Section */}
        <EmployerAnalytics onTabChange={setActiveTab} />

        {/* Referral Program */}
        <ReferralCard user={user} />

        {/* Search Section */}
        <div className="card mb-8" data-testid="search-section">
          {/* Tabs */}
          <div className="flex gap-2 mb-4 border-b-2 border-slate-200 pb-1 overflow-x-auto">
            <button
              onClick={() => setActiveTab('search')}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                activeTab === 'search'
                  ? 'bg-orange-50 text-orange-600 border-orange-500'
                  : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
              }`}
              data-testid="tab-search"
            >
              <Search className="h-4 w-4" />
              <span className="text-sm sm:text-base">Find Tradespeople</span>
            </button>
            <button
              onClick={() => { setActiveTab('favorites'); fetchFavorites(); }}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                activeTab === 'favorites'
                  ? 'bg-orange-50 text-orange-600 border-orange-500'
                  : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
              }`}
              data-testid="tab-favorites"
            >
              <Heart className="h-4 w-4" />
              <span className="text-sm sm:text-base">My Favorites</span>
              {favorites.length > 0 && (
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  {favorites.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('hired')}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                activeTab === 'hired'
                  ? 'bg-orange-50 text-orange-600 border-orange-500'
                  : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
              }`}
              data-testid="tab-hired"
            >
              <Briefcase className="h-4 w-4" />
              <span className="text-sm sm:text-base">Hired</span>
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                activeTab === 'reviews'
                  ? 'bg-orange-50 text-orange-600 border-orange-500'
                  : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
              }`}
              data-testid="tab-reviews"
            >
              <Star className="h-4 w-4" />
              <span className="text-sm sm:text-base">Reviews</span>
            </button>
            {/* Company tab - only for Business/Enterprise plans */}
            {subscriptionStatus?.plan?.plan_id && ['plan_business', 'plan_enterprise'].includes(subscriptionStatus.plan.plan_id) && (
              <button
                onClick={() => setActiveTab('company')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                  activeTab === 'company'
                    ? 'bg-orange-50 text-orange-600 border-orange-500'
                    : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
                }`}
                data-testid="tab-company"
              >
                <Users className="h-4 w-4" />
                <span className="text-sm sm:text-base">Company</span>
              </button>
            )}
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                activeTab === 'settings'
                  ? 'bg-orange-50 text-orange-600 border-orange-500'
                  : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
              }`}
              data-testid="tab-settings"
            >
              <Settings className="h-4 w-4" />
              <span className="text-sm sm:text-base">Settings</span>
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-semibold transition-all whitespace-nowrap border-b-2 -mb-1 ${
                activeTab === 'billing'
                  ? 'bg-orange-50 text-orange-600 border-orange-500'
                  : 'text-slate-700 hover:bg-slate-50 border-transparent hover:text-slate-900'
              }`}
              data-testid="tab-billing"
            >
              <CreditCard className="h-4 w-4" />
              <span className="text-sm sm:text-base">Billing</span>
            </button>
          </div>

          {activeTab === 'settings' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Notification Preferences</h3>
              <NotificationSettings />
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Billing & Payments</h3>
              {/* FREE MARKETPLACE: BillingManagement hidden; dormant. */}
              {false && <BillingManagement user={user} />}
            </div>
          )}

          {activeTab === 'hired' && (
            <div className="space-y-6">
              <ConfirmedJobsSection userType="employer" />
              <EmploymentHistory />
            </div>
          )}

          {activeTab === 'reviews' && (
            <PendingReviews 
              onReviewSubmit={(tradespersonId) => {
                setSelectedTradesperson({ user_id: tradespersonId });
                setShowReviewModal(true);
              }}
            />
          )}

          {activeTab === 'company' && (
            <CompanyManagement />
          )}

          {activeTab === 'search' && (
            <>
              {/* Free Preview Banner */}
              <FreePreviewBanner />
              
              {/* SIMPLIFIED SEARCH FORM */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6 mb-6">
                {/* Primary Search Fields - Always Visible */}
                <div className="space-y-4">
                  {/* Trade Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Trade <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={searchParams.trade}
                      onChange={(e) => {
                        setSearchParams({ ...searchParams, trade: e.target.value });
                        setSearchError('');
                      }}
                      className={`w-full h-12 px-4 rounded-lg border ${searchError && !searchParams.trade ? 'border-red-300 bg-red-50' : 'border-slate-200'} focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20`}
                      data-testid="search-trade-select"
                    >
                      <option value="">Select trade type</option>
                      {trades.map((trade) => (
                        <option key={trade.trade_id} value={trade.name}>
                          {trade.name}
                        </option>
                      ))}
                    </select>
                    {searchError && !searchParams.trade && (
                      <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {searchError}
                      </p>
                    )}
                  </div>
                  
                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      <MapPin className="h-4 w-4 inline mr-1" />
                      Location
                    </label>
                    <input
                      type="text"
                      value={searchParams.location}
                      onChange={handleLocationChange}
                      placeholder="e.g. SW1A 1AA or London"
                      className="w-full h-12 px-4 rounded-lg border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 uppercase"
                      data-testid="search-location-input"
                    />
                  </div>
                  
                  {/* Date Selection - Single date by default */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-medium text-slate-700">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        Date needed
                      </label>
                      <button
                        type="button"
                        onClick={() => setUseDateRange(!useDateRange)}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                      >
                        {useDateRange ? 'Single date' : 'Date range'}
                      </button>
                    </div>
                    <div className={`grid gap-3 ${useDateRange ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <input
                        type="date"
                        value={searchParams.start_date}
                        onChange={(e) => setSearchParams({ ...searchParams, start_date: e.target.value })}
                        className="w-full h-12 px-4 rounded-lg border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                        data-testid="search-start-date"
                        min={todayYMD()}
                      />
                      {useDateRange && (
                        <input
                          type="date"
                          value={searchParams.end_date}
                          onChange={(e) => setSearchParams({ ...searchParams, end_date: e.target.value })}
                          className="w-full h-12 px-4 rounded-lg border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                          data-testid="search-end-date"
                          min={searchParams.start_date || todayYMD()}
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Urgent Requirement Section - Simplified */}
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-emerald-800">Urgent Requirement</h4>
                        <p className="text-sm text-emerald-600 mt-0.5">Prioritise tradespeople who are available to start quickly.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={searchParams.available_now}
                          onChange={(e) => setSearchParams({ ...searchParams, available_now: e.target.checked })}
                          className="sr-only peer"
                          data-testid="filter-available-now"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                        <span className="ml-2 text-sm font-medium text-emerald-700">Available Now</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* SEARCH BUTTON - Large and prominent on mobile */}
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="w-full h-14 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-300 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 text-lg shadow-lg shadow-orange-500/20 touch-manipulation"
                    data-testid="search-btn"
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-5 w-5" />
                        Search Tradespeople
                      </>
                    )}
                  </button>
                </div>
                
                {/* More Filters - Collapsible */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowMoreFilters(!showMoreFilters)}
                    className="w-full flex items-center justify-between text-sm text-slate-600 hover:text-slate-800"
                    data-testid="toggle-more-filters"
                  >
                    <span className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      More filters
                    </span>
                    {showMoreFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  
                  {showMoreFilters && (
                    <div className="mt-4 space-y-4">
                      {/* Rating Filter */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Minimum Rating</label>
                        <select
                          value={searchParams.min_rating}
                          onChange={(e) => setSearchParams({ ...searchParams, min_rating: e.target.value })}
                          className="w-full h-12 px-4 rounded-lg border border-slate-200 focus:border-orange-500"
                          data-testid="search-min-rating"
                        >
                          <option value="">Any Rating</option>
                          <option value="4">4+ Stars</option>
                          <option value="3">3+ Stars</option>
                          <option value="2">2+ Stars</option>
                        </select>
                      </div>
                      
                      {/* Credentials Verified Filter */}
                      <label className="flex items-center gap-3 cursor-pointer" title="Show only tradespeople where OnSiteHand has verified at least one submitted credential. Not a guarantee of workmanship, conduct, availability or suitability.">
                        <input
                          type="checkbox"
                          checked={searchParams.verified_only}
                          onChange={(e) => setSearchParams({ ...searchParams, verified_only: e.target.checked, has_credentials: e.target.checked })}
                          className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                          data-testid="filter-verified-only"
                        />
                        <div>
                          <span className="text-sm font-medium text-slate-700">Credentials verified only</span>
                          <p className="text-xs text-slate-500">OnSiteHand has checked at least one submitted credential. Not a guarantee of workmanship, conduct, availability or suitability.</p>
                        </div>
                      </label>
                      
                      {/* Daily Rate Range */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Min Daily Rate (£)</label>
                          <input
                            type="number"
                            value={searchParams.min_rate}
                            onChange={(e) => setSearchParams({ ...searchParams, min_rate: e.target.value })}
                            placeholder="e.g. 100"
                            className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-orange-500 text-sm"
                            data-testid="filter-min-rate"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Max Daily Rate (£)</label>
                          <input
                            type="number"
                            value={searchParams.max_rate}
                            onChange={(e) => setSearchParams({ ...searchParams, max_rate: e.target.value })}
                            placeholder="e.g. 300"
                            className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-orange-500 text-sm"
                            data-testid="filter-max-rate"
                          />
                        </div>
                      </div>
                      
                      {/* Transport Filter */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Transport</label>
                        <select
                          value={searchParams.transport}
                          onChange={(e) => setSearchParams({ ...searchParams, transport: e.target.value })}
                          className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-orange-500 text-sm"
                          data-testid="filter-transport"
                        >
                          <option value="">Any Transport</option>
                          <option value="Own vehicle">Own Vehicle</option>
                          <option value="Public transport">Public Transport</option>
                          <option value="Bicycle">Bicycle</option>
                          <option value="Walking">Walking</option>
                        </select>
                      </div>
                      
                      {/* Credential Type Filter */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Credential Type</label>
                        <select
                          value={searchParams.credential_type}
                          onChange={(e) => setSearchParams({ ...searchParams, credential_type: e.target.value, has_credentials: !!e.target.value })}
                          className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-orange-500 text-sm"
                          data-testid="filter-credential"
                        >
                          <option value="">Any Credentials</option>
                          <option value="Gas Safe">Gas Safe</option>
                          <option value="CSCS Card">CSCS Card</option>
                          <option value="City & Guilds">City & Guilds</option>
                          <option value="NVQ">NVQ</option>
                          <option value="Electrical Certification">Electrical Certification</option>
                          <option value="NICEIC">NICEIC</option>
                          <option value="Part P">Part P</option>
                        </select>
                      </div>
                      
                      {/* Engagement Type - multi-select checkboxes */}
                      <div data-testid="engagement-type-filters">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Engagement type</label>
                        <p className="text-xs text-slate-500 mb-2">How do you want to engage with the trade?</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { key: 'engagement_day_rate', label: 'Day rate' },
                            { key: 'engagement_price_work', label: 'Price work' },
                            { key: 'engagement_labour_only', label: 'Labour only' },
                            { key: 'engagement_supply_and_fit', label: 'Supply & fit' },
                            { key: 'engagement_subcontractor', label: 'Subcontractor' },
                            { key: 'engagement_team', label: 'Team / gang' },
                            { key: 'engagement_ltd', label: 'Limited company' },
                          ].map((opt) => (
                            <label
                              key={opt.key}
                              className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 hover:bg-slate-100 rounded-lg px-3 py-2 border border-slate-200"
                            >
                              <input
                                type="checkbox"
                                checked={!!searchParams[opt.key]}
                                onChange={(e) =>
                                  setSearchParams({ ...searchParams, [opt.key]: e.target.checked })
                                }
                                className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                data-testid={`filter-${opt.key.replace('engagement_', 'engagement-')}`}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Worker Type Filter (single-select — advanced) */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Worker type (advanced)</label>
                        <select
                          value={searchParams.worker_type}
                          onChange={(e) => setSearchParams({ ...searchParams, worker_type: e.target.value })}
                          className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-orange-500 text-sm"
                          data-testid="filter-worker-type"
                        >
                          <option value="">Any</option>
                          <option value="individual_tradesperson">Individual tradesperson</option>
                          <option value="self_employed_subcontractor">Self-employed subcontractor</option>
                          <option value="small_trade_team_gang">Small trade team/gang</option>
                          <option value="limited_company_trade_contractor">Limited company trade contractor</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Save Search - De-emphasized, shown only after search with results */}
                {tradespeople.length > 0 && searchParams.trade && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleSaveSearch}
                      className="text-sm text-slate-500 hover:text-orange-600 flex items-center gap-1.5"
                      data-testid="save-search-btn"
                    >
                      <Bookmark className="h-4 w-4" />
                      Save this search
                    </button>
                    {searchAlerts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowAlertsModal(true)}
                        className="text-xs text-orange-600 hover:text-orange-700"
                        data-testid="view-alerts-btn"
                      >
                        {searchAlerts.length} alert{searchAlerts.length > 1 ? 's' : ''} active
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Empty State - No Results */}
              {!isSearching && searchParams.trade && tradespeople.length === 0 && (
                <div className="bg-slate-50 rounded-xl p-8 text-center mb-6">
                  <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No tradespeople found</h3>
                  <p className="text-slate-500 mb-4">Try adjusting your search criteria:</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Try a different location or wider area</li>
                    <li>• Remove the date filter to see more results</li>
                    <li>• Turn off "Available Now" to include more tradespeople</li>
                  </ul>
                </div>
              )}
            </>
          )}

          {activeTab === 'favorites' && (
            <div className="text-slate-600">
              {favorites.length === 0 ? (
                <div className="text-center py-8">
                  <Heart className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="font-medium text-slate-700 mb-1">{get('employer.empty.no_favourites', 'No saved tradespeople yet')}</p>
                  <p className="text-sm text-slate-500">{get('employer.empty.no_favourites_desc', 'Save tradespeople you like to contact them later.')}</p>
                </div>
              ) : (
                <p className="mb-4">{favorites.length} saved tradesperson{favorites.length > 1 ? 's' : ''}</p>
              )}
            </div>
          )}
        </div>

        {/* Tradespeople Results */}
        {((activeTab === 'search' && getFilteredAndSortedResults(tradespeople).length > 0) || (activeTab === 'favorites' && favorites.length > 0)) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tradespeople-results">
            {(activeTab === 'search' ? getFilteredAndSortedResults(tradespeople) : favorites).map((person) => (
              <div key={person.user_id} className="card hover:shadow-lg transition-shadow" data-testid={`tradesperson-card-${person.user_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    {/* Display name */}
                    <h4 className="text-lg font-bold text-slate-900 mb-1">
                      {person.display_name || person.user_info?.name}
                    </h4>
                    <p className="text-orange-500 font-medium mb-2">{person.trade}</p>
                    
                    {/* Supply/Demand Indicators */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {/* On Call Badge - Most prominent */}
                      {person.is_on_call && (
                        <span className="inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-bold" data-testid="on-call-badge">
                          <Phone className="h-3 w-3" />
                          ON CALL
                        </span>
                      )}
                      {/* Priority Boost Badge */}
                      {person.is_priority_boosted && (
                        <span className="inline-flex items-center gap-1 bg-orange-100 border border-orange-300 text-orange-800 px-2 py-0.5 rounded-full text-xs font-bold" data-testid="priority-boost-badge">
                          PRIORITY
                        </span>
                      )}
                      {/* Quick Responder Badge */}
                      <QuickResponderBadge userId={person.user_id} size="xs" showTooltip={true} />
                      {person.is_high_demand && (
                        <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                          </svg>
                          High Demand
                        </span>
                      )}
                      {person.recently_contacted && (
                        <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          <MessageSquare className="h-3 w-3" />
                          Recently Contacted
                        </span>
                      )}
                      {person.has_contacted && (
                        <span className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          <CheckCircle className="h-3 w-3" />
                          Connected
                        </span>
                      )}
                      {/* Worker Type Badge */}
                      {person.worker_type && person.worker_type !== 'individual_tradesperson' && (
                        <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-xs font-medium" data-testid={`worker-type-badge-${person.user_id}`}>
                          {person.worker_type === 'self_employed_subcontractor' && 'Subcontractor'}
                          {person.worker_type === 'small_trade_team_gang' && 'Trade Team'}
                          {person.worker_type === 'limited_company_trade_contractor' && 'Ltd Company'}
                        </span>
                      )}
                    </div>
                    
                    {/* Verification Status Badge */}
                    {person.is_verified ? (
                      <div className="mb-3">
                        <div
                          className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-lg font-semibold text-sm mb-1"
                          title="OnSiteHand has checked the evidence submitted for at least one credential. Not a guarantee of workmanship, conduct, availability or suitability."
                          data-testid={`credentials-verified-badge-${person.user_id}`}
                        >
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Credentials verified
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug">
                          OnSiteHand has checked submitted evidence for at least one credential. Not a guarantee of workmanship, conduct, availability or suitability.
                        </p>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <span
                          className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-sm font-medium"
                          data-testid={`not-verified-badge-${person.user_id}`}
                        >
                          <AlertCircle className="h-4 w-4" />
                          Not verified by OnSiteHand
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFavorite(person.user_id, person.is_favorite)}
                      className={`p-1.5 rounded-full transition-colors ${
                        person.is_favorite 
                          ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                          : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                      }`}
                      title={person.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                      data-testid={`favorite-btn-${person.user_id}`}
                    >
                      <Heart className={`h-5 w-5 ${person.is_favorite ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Rating and Jobs - Always visible */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    <span className="font-bold text-slate-900">{person.average_rating || 0}</span>
                  </div>
                  {(person.completed_jobs || person.total_reviews) ? (
                    <span className="text-sm text-slate-600">
                      ({person.completed_jobs || person.total_reviews || 0} jobs completed)
                    </span>
                  ) : (
                    <span className="text-sm text-slate-600">
                      • Trusted by employers
                    </span>
                  )}
                </div>

                {/* Highlighted Quote - Only for verified */}
                {person.is_verified && person.highlighted_quote && (
                  <div className="bg-slate-50 border-l-2 border-orange-400 p-2 rounded-r mb-3">
                    <p className="text-sm text-slate-600 italic line-clamp-2">
                      "{person.highlighted_quote}"
                    </p>
                  </div>
                )}

                {/* Key Stats - Always visible */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Day rate</span>
                    <span className="font-semibold text-slate-900">£{person.daily_rate || person.day_rate || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Work areas</span>
                    <span className="text-slate-700">{person.work_areas?.slice(0, 2).join(', ') || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Availability</span>
                    <span className={`font-medium ${
                      person.is_on_call ? 'text-emerald-700' :
                      person.availability_mode === 'available_now' ? 'text-green-600' :
                      person.availability_mode === 'busy' ? 'text-amber-600' : 'text-slate-700'
                    }`} data-testid={`availability-badge-${person.user_id}`}>
                      {person.availability_summary
                        || (person.availability_mode === 'available_now' ? 'Available now'
                            : person.availability_mode === 'available_from' && person.available_from_date
                              ? `Available from ${new Date(person.available_from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                              : person.availability_mode === 'custom_dates' && person.available_dates?.length > 0
                                ? `Available on ${person.available_dates.length} date${person.available_dates.length !== 1 ? 's' : ''}`
                                : person.availability_mode === 'busy'
                                  ? 'Currently busy'
                                  : 'Check availability')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Transport</span>
                    <span className="text-slate-700">{person.has_transport || person.transport ? 'Own transport' : 'No transport'}</span>
                  </div>
                </div>

                {/* Contact Status Indicator */}
                {person.has_contacted && (
                  <div className="bg-green-50 text-green-700 text-xs font-medium px-2 py-1 rounded mb-3 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Reviews unlocked
                  </div>
                )}

                {/* On Call premium notice */}
                {person.is_on_call && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2.5 py-1.5 rounded-lg mb-3" data-testid={`on-call-premium-notice-${person.user_id}`}>
                    <p className="font-medium">Available for urgent work</p>
                    <p className="text-emerald-600 mt-0.5">On Call trades use premium credits to view or contact.</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/profile/${person.user_id}`)}
                    className="btn-secondary flex-1 text-sm"
                    data-testid={`view-profile-btn-${person.user_id}`}
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => handleContactTradesperson(person)}
                    className="btn-primary flex-1 text-sm"
                    data-testid={`contact-btn-${person.user_id}`}
                  >
                    {person.has_contacted ? 'Continue Conversation' : 'Start Hire Conversation'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No results from filters */}
        {tradespeople.length > 0 && getFilteredAndSortedResults(tradespeople).length === 0 && activeTab === 'search' && (
          <div className="card text-center py-12" data-testid="no-filtered-results">
            <Filter className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-700 mb-2">No exact matches right now</p>
            <p className="text-slate-500 text-sm mb-4">
              {tradespeople.length} tradesperson{tradespeople.length !== 1 ? 's' : ''} found, but none available {availabilityFilters.availableTomorrow ? 'tomorrow' : 'this week'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setAvailabilityFilters({ availableTomorrow: false, availableThisWeek: false })}
                className="text-orange-600 hover:text-orange-700 font-medium text-sm"
              >
                Clear filters to see all
              </button>
              {searchParams.trade && searchParams.location && (
                <>
                  <span className="hidden sm:inline text-slate-300">|</span>
                  <button
                    onClick={handleSaveSearch}
                    className="flex items-center gap-1.5 text-orange-600 hover:text-orange-700 font-medium text-sm"
                  >
                    <BellRing className="h-4 w-4" />
                    Alert me when someone's free
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {tradespeople.length === 0 && subscription?.active && activeTab === 'search' && (
          <div className="card text-center py-12" data-testid="no-results">
            <Search className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-700 mb-2">Find your next tradesperson</p>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Select a trade and location above. Add your job dates to see who's available when you need them.
            </p>
          </div>
        )}

        {/* Supply/Demand Message */}
        {activeTab === 'search' && supplyDemand?.has_more && (
          <div className="mt-6 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4" data-testid="supply-demand-message">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Users className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    {supplyDemand.hidden_count} more tradesperson{supplyDemand.hidden_count > 1 ? 's' : ''} available
                  </p>
                  <p className="text-sm text-slate-600">
                    Connect with the visible trades to see more options
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">
                  Showing {supplyDemand.visible_count} of {supplyDemand.total_available}
                </p>
                {supplyDemand.is_high_demand_search && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium mt-1">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                    </svg>
                    High demand search
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent Quote Requests — shown on the Search tab so it surfaces right
            next to messaging context. Returns null when the employer has no
            quote-request conversations. */}
        {activeTab === 'search' && <RecentQuoteRequestsPanel />}

        {/* Recent Activity Feed */}
        {activeTab === 'search' && activityFeed.length > 0 && (
          <div className="mt-8" data-testid="activity-feed">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-orange-500" />
              Recent activity
            </h3>
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {activityFeed.map((activity) => (
                <button 
                  key={activity.activity_id} 
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                  data-testid={`activity-item-${activity.activity_id}`}
                  onClick={() => {
                    // Navigate to profile if tradesperson_id is available
                    if (activity.tradesperson_id) {
                      navigate(`/profile/${activity.tradesperson_id}`);
                    } else if (activity.data?.user_id) {
                      navigate(`/profile/${activity.data.user_id}`);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      activity.type === 'offer_accepted' ? 'bg-green-500' :
                      activity.type === 'offer_sent' ? 'bg-blue-500' :
                      'bg-orange-500'
                    }`} />
                    <span className="text-slate-700">{activity.text_summary}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">
                      {formatRelativeTime(activity.created_at)}
                    </span>
                    {(activity.tradesperson_id || activity.data?.user_id) && (
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Saved Searches Section */}
        {activeTab === 'search' && savedSearches.length > 0 && (
          <div className="mt-8" data-testid="saved-searches">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-orange-500" />
              Saved Searches
              <span className="text-sm font-normal text-slate-500">
                ({savedSearches.filter(s => s.is_enabled).length} active)
              </span>
            </h3>
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {savedSearches.map((search) => (
                <div 
                  key={search.search_id} 
                  className="px-4 py-3 flex items-center justify-between"
                  data-testid={`saved-search-${search.search_id}`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleToggleSavedSearch(search.search_id, search.is_enabled)}
                      className={`transition-colors ${search.is_enabled ? 'text-green-500' : 'text-slate-400'}`}
                      title={search.is_enabled ? 'Notifications enabled' : 'Notifications paused'}
                    >
                      {search.is_enabled ? (
                        <ToggleRight className="h-6 w-6" />
                      ) : (
                        <ToggleLeft className="h-6 w-6" />
                      )}
                    </button>
                    <span className={`text-sm ${search.is_enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                      {formatSavedSearchLabel(search)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteSavedSearch(search.search_id)}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    data-testid={`delete-saved-search-${search.search_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Partner Placements - Employer Dashboard */}
        {activeTab === 'search' && (
          <div className="mt-8" data-testid="employer-partner-placements">
            <PartnerPlacement 
              placement="employer-dashboard" 
              userType="employer" 
              region={user?.postcode}
            />
          </div>
        )}
      </div>

      {/* Hire Modal */}
      <HireModal
        open={showHireModal}
        onClose={() => { setShowHireModal(false); setHireTarget(null); }}
        tradesperson={hireTarget}
        onSuccess={() => {
          // Optionally refresh or switch to hired tab
          if (activeTab === 'hired') {
            // Trigger a refresh in EmploymentHistory component
          }
        }}
      />

      {/* Insufficient Credits Modal */}
      <Dialog open={showNoChatModal} onOpenChange={setShowNoChatModal}>
        <DialogContent className="sm:max-w-md" data-testid="no-credits-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-500" />
              Need More Credits
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-slate-600">
              You need more credits to unlock this profile and contact the tradesperson.
            </p>
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-600 mb-2">Credits unlock:</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>✓ Full tradesperson profiles</li>
                <li>✓ Complete availability calendars</li>
                <li>✓ Direct contact & messaging</li>
              </ul>
            </div>
            <p className="text-sm text-slate-500 text-center">
              Browse profiles for free. OnSiteHand is free for employers and tradespeople.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowNoChatModal(false)}
                className="btn-secondary flex-1"
              >
                Continue Browsing
              </button>
              <button
                onClick={() => {
                  setShowNoChatModal(false);
                  // Navigate to credits/subscription page
                  navigate('/plans');
                }}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="get-credits-btn"
              >
                <Coins className="h-4 w-4" />
                Get Credits
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent data-testid="review-modal">
          <DialogHeader>
            <DialogTitle>Leave a Review for {selectedTradesperson?.user_info?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Punctuality: {reviewData.punctuality}
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={reviewData.punctuality}
                onChange={(e) => setReviewData({ ...reviewData, punctuality: parseInt(e.target.value) })}
                className="w-full"
                data-testid="punctuality-slider"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Professionalism: {reviewData.professionalism}
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={reviewData.professionalism}
                onChange={(e) => setReviewData({ ...reviewData, professionalism: parseInt(e.target.value) })}
                className="w-full"
                data-testid="professionalism-slider"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Quality: {reviewData.quality}
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={reviewData.quality}
                onChange={(e) => setReviewData({ ...reviewData, quality: parseInt(e.target.value) })}
                className="w-full"
                data-testid="quality-slider"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Comment (Optional)</label>
              <textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                rows={3}
                className="w-full"
                placeholder="Share your experience..."
                data-testid="review-comment-textarea"
              />
            </div>
            <button
              onClick={submitReview}
              className="btn-primary w-full"
              data-testid="submit-review-btn"
            >
              Submit Review
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notifications Modal */}
      <Dialog open={showNotificationsModal} onOpenChange={setShowNotificationsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-orange-500" />
              Notifications
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <BellRing className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No notifications yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div
                    key={notif.notification_id}
                    className={`p-4 rounded-lg border ${notif.read ? 'bg-white border-slate-200' : 'bg-orange-50 border-orange-200'}`}
                    data-testid={`notification-${notif.notification_id}`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <p className={`font-medium ${notif.read ? 'text-slate-700' : 'text-orange-800'}`}>
                          {notif.title}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(notif.created_at).toLocaleString('en-GB')}
                        </p>
                      </div>
                      {!notif.read && (
                        <button
                          onClick={() => handleMarkNotificationRead(notif.notification_id)}
                          className="text-xs text-orange-600 hover:text-orange-700"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                    {notif.tradesperson_id && (
                      <button
                        onClick={() => { navigate(`/profile/${notif.tradesperson_id}`); setShowNotificationsModal(false); }}
                        className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-medium"
                      >
                        View Profile →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Search Alerts Modal */}
      <Dialog open={showAlertsModal} onOpenChange={setShowAlertsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-orange-500" />
              My Search Alerts
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">
              {get('employer.alert.set_alert_desc', "We'll notify you when matching tradespeople become available.")}
            </p>
            {searchAlerts.length === 0 ? (
              <div className="text-center py-8">
                <BellRing className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{get('employer.alert.no_alerts', 'No alerts set')}</p>
                <p className="text-sm text-slate-400 mt-1">{get('employer.alert.no_alerts_desc', 'Search with dates and check "Notify me" to create one.')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {searchAlerts.map((alert) => (
                  <div
                    key={alert.alert_id}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                    data-testid={`alert-${alert.alert_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-slate-700">
                          {alert.trade || 'Any Trade'} in {alert.location || 'Any Location'}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          Dates: {new Date(alert.start_date).toLocaleDateString('en-GB')} - {new Date(alert.end_date).toLocaleDateString('en-GB')}
                        </p>
                        {alert.min_rating && (
                          <p className="text-sm text-slate-500">{alert.min_rating}+ stars</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteAlert(alert.alert_id)}
                        className="text-red-500 hover:text-red-600 p-1"
                        title="Delete alert"
                        data-testid={`delete-alert-${alert.alert_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Urgent Request Modal */}
      <Dialog open={showUrgentRequestModal} onOpenChange={setShowUrgentRequestModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-emerald-500" />
              Send Urgent Request
            </DialogTitle>
          </DialogHeader>
          <UrgentRequestForm 
            targetTrade={searchParams.trade}
            subscription={subscription}
            onClose={() => setShowUrgentRequestModal(false)}
            onSuccess={(data) => {
              toast.success(`Urgent request sent to ${data.notifications_sent} On Call tradespeople!`);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditPurchase}
        onClose={() => setShowCreditPurchase(false)}
        packages={creditSettings?.topup_packages || []}
        isFoundingEmployer={user?.is_founding_employer}
        founderBonusPercent={creditSettings?.founder_bonus?.bonus_percent || 20}
      />

      {/* Credit Purchase Success Handler */}
      {purchaseSessionId && (
        <CreditPurchaseSuccess
          sessionId={purchaseSessionId}
          onComplete={handlePurchaseComplete}
        />
      )}
    </div>
  );
}
